import React, { useState, useEffect, useCallback } from 'react';
import { Printer } from 'lucide-react';
import AfterschoolReportModal from './modals/AfterschoolReportModal';

const Reports = ({ apiCall, selectedYear, showError, showSuccess, hasRole }) => {
  const [outstandingReport, setOutstandingReport] = useState([]);
  const [revenueReport, setRevenueReport] = useState([]);
  const [enrollmentReport, setEnrollmentReport] = useState({});
  const [afterschoolStudentsReport, setAfterschoolStudentsReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('outstanding');
  
  // NEW: State for Afterschool Report Modal
  const [showAfterschoolReportModal, setShowAfterschoolReportModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const outstanding = await apiCall(`/reports/outstanding-balances?academicYear=${selectedYear}`);
      setOutstandingReport(outstanding || []);
      
      const revenue = await apiCall(`/reports/revenue?academicYear=${selectedYear}`);
      setRevenueReport(revenue || []);
      
      const enrollment = await apiCall(`/reports/enrollment-summary?academicYear=${selectedYear}`);
      setEnrollmentReport(enrollment || {});

      const afterschoolEnrollments = await apiCall(`/afterschool/enrollments?academicYear=${selectedYear}`);
      const families = await apiCall('/families?academicYear=all');
      
      const studentsWithBalances = afterschoolEnrollments.map(enrollment => {
        const family = families.find(f => f.FamilyID === enrollment.FamilyID);
        return {
          ...enrollment,
          FamilyBalance: family ? family.TotalOutstanding : 0,
          FatherName: family ? family.FatherName : 'N/A',
          PrimaryPhone: family ? family.PrimaryPhone : 'N/A'
        };
      });
      
      setAfterschoolStudentsReport(studentsWithBalances);
    } catch (error) {
      console.error('Error loading reports:', error);
      if (showError) showError('Error loading reports: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, selectedYear, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const exportCSV = (data, filename, headers) => {
    const csv = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportAfterschoolStudentsCSV = () => {
    const data = afterschoolStudentsReport.map(student => [
      student.FamilyID,
      student.FatherName,
      student.FirstName + ' ' + student.LastName,
      student.PackageName,
      student.TimeSlot || 'Not Specified',
      student.BillingType,
      student.FinalCost?.toFixed(2) || '0.00',
      student.AmountPaid?.toFixed(2) || '0.00',
      student.BalanceRemaining?.toFixed(2) || '0.00',
      student.FamilyBalance?.toFixed(2) || '0.00',
      student.PrimaryPhone,
      student.Status
    ]);
    
    exportCSV(
      data,
      `afterschool-students-${selectedYear}.csv`,
      ['Family ID', 'Father Name', 'Student Name', 'Package', 'Time Slot', 'Billing Type', 'Enrollment Cost', 'Paid', 'Enrollment Balance', 'Total Family Balance', 'Phone', 'Status']
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex gap-2 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('outstanding')}
            className={`px-4 py-2 font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'outstanding'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            💰 Outstanding Balances
          </button>
          <button
            onClick={() => setActiveTab('afterschool-students')}
            className={`px-4 py-2 font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'afterschool-students'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            🎨 Afterschool Students
          </button>
          <button
            onClick={() => setActiveTab('afterschool-detailed')}
            className={`px-4 py-2 font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'afterschool-detailed'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📊 Afterschool Detailed
          </button>
          <button
            onClick={() => setActiveTab('revenue')}
            className={`px-4 py-2 font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'revenue'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📈 Revenue
          </button>
          <button
            onClick={() => setActiveTab('enrollment')}
            className={`px-4 py-2 font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'enrollment'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📋 Enrollment Summary
          </button>
        </div>
      </div>

      {/* Outstanding Balances Report */}
      {activeTab === 'outstanding' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Outstanding Balances - {selectedYear === 'all' ? 'All Years' : selectedYear}
            </h2>
            <button 
              onClick={() => exportCSV(
                outstandingReport.map(r => [r.FamilyID, r.FatherName, r.MotherName, r.PrimaryPhone, r.AcademicYear, r.Module, r.TotalCharges, r.TotalPayments, r.Balance]),
                `outstanding-balances-${selectedYear}.csv`,
                ['Family ID', 'Father Name', 'Mother Name', 'Phone', 'Year', 'Module', 'Charges', 'Payments', 'Balance']
              )}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              <Printer size={16} />
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Family</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Father</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Year</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Module</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Charges</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Paid</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Balance</th>
                </tr>
              </thead>
              <tbody>
                {outstandingReport.map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">{item.FamilyID}</td>
                    <td className="px-4 py-3">{item.FatherName}</td>
                    <td className="px-4 py-3">{item.PrimaryPhone}</td>
                    <td className="px-4 py-3">{item.AcademicYear}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        item.Module === 'Bus' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {item.Module}
                      </span>
                    </td>
                    <td className="px-4 py-3">${item.TotalCharges.toFixed(2)}</td>
                    <td className="px-4 py-3 text-green-600">${item.TotalPayments.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className="font-bold text-red-600">${item.Balance.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
                {outstandingReport.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No outstanding balances for selected period
                    </td>
                  </tr>
                )}
              </tbody>
              {outstandingReport.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="5" className="px-4 py-3 text-right">TOTAL:</td>
                    <td className="px-4 py-3">
                      ${outstandingReport.reduce((sum, r) => sum + r.TotalCharges, 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-green-600">
                      ${outstandingReport.reduce((sum, r) => sum + r.TotalPayments, 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-red-600">
                      ${outstandingReport.reduce((sum, r) => sum + r.Balance, 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Afterschool Students Report (Simple Table) */}
      {activeTab === 'afterschool-students' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Afterschool Students - {selectedYear === 'all' ? 'All Years' : selectedYear}
            </h2>
            <button 
              onClick={exportAfterschoolStudentsCSV}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              <Printer size={16} />
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Family</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Father</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Student</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Package</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Time Slot</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Cost</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Paid</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Balance</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {afterschoolStudentsReport.map((student, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">{student.FamilyID}</td>
                    <td className="px-4 py-3">{student.FatherName}</td>
                    <td className="px-4 py-3">{student.FirstName} {student.LastName}</td>
                    <td className="px-4 py-3 text-sm">{student.PackageName}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                        {student.TimeSlot || 'Not Specified'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        {student.BillingType}
                      </span>
                    </td>
                    <td className="px-4 py-3">${(student.FinalCost || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-green-600">${(student.AmountPaid || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`font-bold ${(student.BalanceRemaining || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        ${(student.BalanceRemaining || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{student.PrimaryPhone}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        student.Status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {student.Status}
                      </span>
                    </td>
                  </tr>
                ))}
                {afterschoolStudentsReport.length === 0 && (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                      No afterschool students for selected period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {afterschoolStudentsReport.length > 0 && (
            <div className="mt-6 grid md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Students</p>
                <p className="text-2xl font-bold text-blue-600">{afterschoolStudentsReport.length}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">
                  ${afterschoolStudentsReport.reduce((sum, s) => sum + (s.AmountPaid || 0), 0).toFixed(2)}
                </p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Enrollment Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">
                  ${afterschoolStudentsReport.reduce((sum, s) => sum + (s.BalanceRemaining || 0), 0).toFixed(2)}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Families w/ Balance</p>
                <p className="text-2xl font-bold text-red-600">
                  {afterschoolStudentsReport.filter(s => (s.FamilyBalance || 0) > 0).length}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* NEW: Afterschool Detailed Report */}
      {activeTab === 'afterschool-detailed' && (
        <div className="space-y-6">
          {/* Banner */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold mb-2">Afterschool Detailed Report</h2>
                <p className="text-green-100">Comprehensive analysis by package, time slot, and student status</p>
              </div>
              <button
                onClick={() => setShowAfterschoolReportModal(true)}
                className="bg-white text-green-600 hover:bg-green-50 px-6 py-3 rounded-lg font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
              >
                <Printer size={20} />
                Open Full Report
              </button>
            </div>
          </div>

          {/* Quick Stats Preview */}
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600">
              <p className="text-gray-600 text-sm mb-1">Total Enrolled</p>
              <p className="text-3xl font-bold text-gray-800">
                {afterschoolStudentsReport.length}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600">
              <p className="text-gray-600 text-sm mb-1">Active Students</p>
              <p className="text-3xl font-bold text-gray-800">
                {afterschoolStudentsReport.filter(s => s.Status === 'Active').length}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-600">
              <p className="text-gray-600 text-sm mb-1">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-800">
                ${afterschoolStudentsReport.reduce((sum, s) => sum + (s.AmountPaid || 0), 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-600">
              <p className="text-gray-600 text-sm mb-1">Outstanding</p>
              <p className="text-3xl font-bold text-gray-800">
                ${afterschoolStudentsReport.reduce((sum, s) => sum + (s.BalanceRemaining || 0), 0).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Instructions Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-bold text-blue-800 mb-2">📊 Available in Full Report</h3>
            <ul className="space-y-2 text-sm text-blue-700">
              <li>✓ Enrollment breakdown by package with revenue analysis</li>
              <li>✓ Students grouped by time slot</li>
              <li>✓ Detailed student lists for each package</li>
              <li>✓ Advanced filters (year, package, time slot, status)</li>
              <li>✓ Printable and exportable (PDF & CSV)</li>
            </ul>
            <p className="mt-4 text-sm text-blue-600">
              Click <strong>"Open Full Report"</strong> above to access the comprehensive report with all analytics and export options.
            </p>
          </div>
        </div>
      )}

      {/* Revenue Report */}
      {activeTab === 'revenue' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Revenue Report - {selectedYear === 'all' ? 'All Years' : selectedYear}
            </h2>
            <button 
              onClick={() => exportCSV(
                revenueReport.map(r => [r.AcademicYear, r.Module, r.PaymentYear, r.PaymentMonth, r.TotalPayments, r.TotalAmount]),
                `revenue-report-${selectedYear}.csv`,
                ['Year', 'Module', 'Year', 'Month', 'Total Payments', 'Total Amount']
              )}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              <Printer size={16} />
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Academic Year</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Module</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Period</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Payments Count</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {revenueReport.map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">{item.AcademicYear}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        item.Module === 'Bus' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {item.Module}
                      </span>
                    </td>
                    <td className="px-4 py-3">{item.PaymentYear}-{String(item.PaymentMonth).padStart(2, '0')}</td>
                    <td className="px-4 py-3">{item.TotalPayments}</td>
                    <td className="px-4 py-3 font-bold text-green-600">${item.TotalAmount.toFixed(2)}</td>
                  </tr>
                ))}
                {revenueReport.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      No revenue data for selected period
                    </td>
                  </tr>
                )}
              </tbody>
              {revenueReport.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="3" className="px-4 py-3 text-right">TOTAL:</td>
                    <td className="px-4 py-3">
                      {revenueReport.reduce((sum, r) => sum + r.TotalPayments, 0)}
                    </td>
                    <td className="px-4 py-3 text-green-600">
                      ${revenueReport.reduce((sum, r) => sum + r.TotalAmount, 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Enrollment Summary */}
      {activeTab === 'enrollment' && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Afterschool Summary - {selectedYear === 'all' ? 'All Years' : selectedYear}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Year</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Enrollments</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Revenue</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {(enrollmentReport.afterschool || []).map((item, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold">{item.AcademicYear}</td>
                      <td className="px-4 py-3">{item.TotalEnrollments}</td>
                      <td className="px-4 py-3 text-green-600">${item.TotalRevenue.toFixed(2)}</td>
                      <td className="px-4 py-3 text-red-600">${item.TotalOutstanding.toFixed(2)}</td>
                    </tr>
                  ))}
                  {(!enrollmentReport.afterschool || enrollmentReport.afterschool.length === 0) && (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                        No afterschool enrollment data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Bus Summary - {selectedYear === 'all' ? 'All Years' : selectedYear}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Year</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">T</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Enrollments</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(enrollmentReport.bus || []).map((item, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold">{item.AcademicYear}</td>
                      <td className="px-4 py-3">{item.Trimester}</td>
                      <td className="px-4 py-3">{item.TotalEnrollments}</td>
                      <td className="px-4 py-3 text-green-600">${item.TotalRevenue.toFixed(2)}</td>
</tr>
                  ))}
                  {(!enrollmentReport.bus || enrollmentReport.bus.length === 0) && (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                        No bus enrollment data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Afterschool Report Modal */}
      {showAfterschoolReportModal && (
        <AfterschoolReportModal
          apiCall={apiCall}
          selectedYear={selectedYear}
          onClose={() => setShowAfterschoolReportModal(false)}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}
    </div>
  );
};

export default Reports;